/**
 * Configuration utility functions.
 * Helpers for working with configuration schemas and settings.
 */

import { ConfigVisibility, ConfigCategory, ConfigMetadata } from './types';
import { ALL_CONFIG_SCHEMAS } from './index';

// Re-export types and enums for convenience
export { ConfigVisibility, ConfigCategory };
export type { ConfigMetadata };

/**
 * Get all settings visible to players.
 */
export function getPlayerVisibleSettings() {
  const playerSettings: Array<{
    category: ConfigCategory;
    key: string;
    metadata: ConfigMetadata;
  }> = [];

  for (const [configName, schema] of Object.entries(ALL_CONFIG_SCHEMAS)) {
    for (const [key, metadata] of Object.entries(schema)) {
      if (metadata.visibility === ConfigVisibility.PLAYER) {
        playerSettings.push({
          category: metadata.category,
          key: `${configName}.${key}`,
          metadata,
        });
      }
    }
  }

  return playerSettings;
}

/**
 * Get all settings visible to developers.
 */
export function getDeveloperVisibleSettings() {
  const devSettings: Array<{
    category: ConfigCategory;
    key: string;
    metadata: ConfigMetadata;
  }> = [];

  for (const [configName, schema] of Object.entries(ALL_CONFIG_SCHEMAS)) {
    for (const [key, metadata] of Object.entries(schema)) {
      if (
        metadata.visibility === ConfigVisibility.DEVELOPER ||
        metadata.visibility === ConfigVisibility.DEVELOPER_HIDDEN
      ) {
        devSettings.push({
          category: metadata.category,
          key: `${configName}.${key}`,
          metadata,
        });
      }
    }
  }

  return devSettings;
}

/**
 * Get settings grouped by category.
 */
export function getSettingsByCategory(visibility: ConfigVisibility | ConfigVisibility[]) {
  const visibilities = Array.isArray(visibility) ? visibility : [visibility];
  const settings: Record<
    ConfigCategory,
    Array<{ key: string; metadata: ConfigMetadata }>
  > = {} as Record<ConfigCategory, Array<{ key: string; metadata: ConfigMetadata }>>;

  for (const [configName, schema] of Object.entries(ALL_CONFIG_SCHEMAS)) {
    for (const [key, metadata] of Object.entries(schema)) {
      if (visibilities.includes(metadata.visibility)) {
        if (!settings[metadata.category]) {
          settings[metadata.category] = [];
        }
        settings[metadata.category].push({
          key: `${configName}.${key}`,
          metadata,
        });
      }
    }
  }

  return settings;
}
